export type LocaleCode = "en" | "ru" | "uk" | "ka";

export type TeamMemberTranslations = Record<string, string>;

export interface TeamDepartmentDto {
    id: number;
    name: TeamMemberTranslations;
    color: string | null;
    order: number;
    created_at: string;
    updated_at: string;
}

export interface TeamMemberDto {
    id: number;
    department_ids: number[];
    departments?: TeamDepartmentDto[];
    name: TeamMemberTranslations;
    surname: TeamMemberTranslations;
    position: TeamMemberTranslations;
    country?: TeamMemberTranslations;
    description?: TeamMemberTranslations;
    order: number;
    photo: string | null;
    photo_url?: string | null;
    created_at: string;
    updated_at: string;
}

export interface TeamMemberPayload {
    department_ids?: number[];
    name: TeamMemberTranslations;
    surname: TeamMemberTranslations;
    position: TeamMemberTranslations;
    country?: TeamMemberTranslations;
    description?: TeamMemberTranslations;
    order?: number;
    photo?: File | null;
}

export const teamMembersTags = ["TeamMembers", "TeamDepartments"] as const;

export type TeamDepartmentPayload = {
    name: TeamMemberTranslations;
    color?: string | null;
    order?: number;
};

const buildFormData = (payload: TeamMemberPayload): FormData => {
    const formData = new FormData();
    if (payload.department_ids && payload.department_ids.length > 0) {
        payload.department_ids.forEach((id) => {
            formData.append("department_ids[]", String(id));
        });
    }
    formData.append("name", JSON.stringify(payload.name));
    formData.append("surname", JSON.stringify(payload.surname));
    formData.append("position", JSON.stringify(payload.position));
    formData.append("country", JSON.stringify(payload.country ?? {}));
    formData.append("description", JSON.stringify(payload.description ?? {}));

    if (typeof payload.order === "number") {
        formData.append("order", String(payload.order));
    }

    if (payload.photo) {
        formData.append("photo", payload.photo);
    }

    return formData;
};

export const teamMembersEndpoints = {
    getTeamMembers: {
        query: () => "/admin/team",
    },
    createTeamMember: {
        query: (payload: TeamMemberPayload) => ({
            url: "/admin/team",
            method: "POST",
            body: buildFormData(payload),
        }),
    },
    updateTeamMember: {
        query: (params: { id: number; data: TeamMemberPayload }) => ({
            url: `/admin/team/${params.id}`,
            method: "POST",
            body: (() => {
                const formData = buildFormData(params.data);
                formData.append("_method", "PUT");
                return formData;
            })(),
        }),
    },
    deleteTeamMember: {
        query: (id: number) => ({
            url: `/admin/team/${id}`,
            method: "DELETE",
        }),
    },
    getTeamDepartments: {
        query: () => "/admin/team/departments",
    },
    createTeamDepartment: {
        query: (payload: TeamDepartmentPayload) => ({
            url: "/admin/team/departments",
            method: "POST",
            body: { name: payload.name, color: payload.color ?? null, order: payload.order ?? undefined },
        }),
    },
    updateTeamDepartment: {
        query: (params: { id: number; data: TeamDepartmentPayload }) => ({
            url: `/admin/team/departments/${params.id}`,
            method: "PUT",
            body: { name: params.data.name, color: params.data.color ?? null, order: params.data.order ?? undefined },
        }),
    },
    deleteTeamDepartment: {
        query: (id: number) => ({
            url: `/admin/team/departments/${id}`,
            method: "DELETE",
        }),
    },
};

